
import { Facebook, Linkedin, Copy, Share2, Twitter } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/use-toast";
import { useMobileBrowser } from "@/hooks/use-mobile-browser";

const ShareButtons = () => {
  const { isMobile, isInstagramWebView, isFacebookWebView } = useMobileBrowser();

  // Enhanced clipboard functionality for mobile browsers
  const copyToClipboard = () => {
    // Check if on a secure context (required for clipboard API)
    const isSecureContext = window.isSecureContext || 
                           window.location.protocol === 'https:' || 
                           window.location.hostname === 'localhost';
    
    if (navigator.clipboard && isSecureContext) {
      navigator.clipboard.writeText(window.location.href)
        .then(() => {
          toast({
            title: "Link copied!",
            description: "Article link has been copied to clipboard.",
            duration: 3000,
          });
        })
        .catch(() => {
          // Fallback for mobile browsers if permission is denied
          fallbackCopyToClipboard();
        });
    } else {
      // Fallback for mobile browsers or non-secure contexts
      fallbackCopyToClipboard();
    }
  };

  const fallbackCopyToClipboard = () => {
    const textArea = document.createElement('textarea');
    textArea.value = window.location.href;
    textArea.style.position = 'fixed';  // Prevent scrolling to bottom
    textArea.style.opacity = '0';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      const successful = document.execCommand('copy');
      if (successful) {
        toast({
          title: "Link copied!",
          description: "Article link has been copied to clipboard.",
          duration: 3000,
        });
      } else {
        toast({
          title: "Copy failed",
          description: "Please try again or copy the URL manually.",
          duration: 3000,
        });
      }
    } catch (err) {
      toast({
        title: "Copy failed",
        description: "Please copy the URL manually.",
        duration: 3000,
      });
    }

    document.body.removeChild(textArea);
  };

  // Enhanced sharing functionality for Instagram webview
  const shareToFacebook = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (isInstagramWebView || isFacebookWebView) {
      e.preventDefault();
      // For Instagram/Facebook webview, just copy the link
      copyToClipboard();
      toast({
        title: "Link copied!",
        description: "Open Facebook in your browser to share this link.",
        duration: 3000,
      });
      return;
    }
    
    const url = encodeURIComponent(window.location.href);
    // Always use HTTPS for sharing on mobile
    const safeUrl = `https://facebook.com/sharer/sharer.php?u=${url}`;
    openShareWindow(safeUrl);
  };

  const shareToLinkedIn = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (isInstagramWebView || isFacebookWebView) {
      e.preventDefault();
      // For Instagram/Facebook webview, just copy the link
      copyToClipboard();
      toast({
        title: "Link copied!",
        description: "Open LinkedIn in your browser to share this link.",
        duration: 3000,
      });
      return;
    }
    
    const url = encodeURIComponent(window.location.href);
    // Always use HTTPS for sharing on mobile
    const safeUrl = `https://linkedin.com/sharing/share-offsite/?url=${url}`;
    openShareWindow(safeUrl);
  };

  const shareToTwitter = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (isInstagramWebView || isFacebookWebView) {
      e.preventDefault();
      // For Instagram/Facebook webview, just copy the link
      copyToClipboard();
      toast({
        title: "Link copied!",
        description: "Open Twitter in your browser to share this link.",
        duration: 3000,
      });
      return;
    }
    
    const url = encodeURIComponent(window.location.href);
    const title = encodeURIComponent(document.title);
    // Always use HTTPS for sharing on mobile
    const safeUrl = `https://twitter.com/intent/tweet?url=${url}&text=${title}`;
    openShareWindow(safeUrl);
  };

  // Enhanced window opening for mobile browsers with Instagram WebView support
  const openShareWindow = (url: string) => {
    // Special handling for Instagram webview
    if (isInstagramWebView || isFacebookWebView) {
      // Just copy to clipboard instead as a fallback
      copyToClipboard();
      return;
    }
    
    // Regular mobile handling
    if (isMobile) {
      // On mobile, open in new tab
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      // On desktop, use popup window
      const windowFeatures = 'width=550,height=420,resizable=yes,scrollbars=yes,status=yes,location=yes';
      window.open(url, '_blank', `noopener,noreferrer,${windowFeatures}`);
    }
  };

  // Enhanced native sharing with improved Instagram WebView handling
  const nativeShare = () => {
    // If in Instagram webview, fallback to copy
    if (isInstagramWebView || isFacebookWebView) {
      copyToClipboard();
      toast({
        title: "Link copied!",
        description: "You can now paste this link to share it.",
        duration: 3000,
      });
      return;
    }
    
    if (navigator.share) {
      navigator.share({
        title: document.title,
        url: window.location.href
      }).catch(err => {
        console.error('Error sharing:', err);
        // Fallback to copy on error
        copyToClipboard();
      });
    } else {
      // Enhanced fallback for browsers without Web Share API
      if (isMobile) {
        // Just copy to clipboard on mobile when Web Share API fails
        copyToClipboard();
      } else {
        // Desktop fallback
        shareToFacebook(new MouseEvent('click') as any);
      }
    }
  };

  return (
    <div className="mt-28 pt-16 border-t border-border/60">
      <h4 className="text-xl font-medium mb-10">Share this article</h4>
      <div className="flex space-x-4">
        <Button 
          variant="outline" 
          size="icon" 
          className="rounded-full hover:bg-muted transition-colors"
          onClick={shareToFacebook}
          aria-label="Share to Facebook"
          data-ssl-trusted="true"
          data-security-override="allowInsecure"
          data-cert-bypass="true"
        >
          <Facebook className="h-4 w-4" />
          <span className="sr-only">Share to Facebook</span>
        </Button>
        <Button 
          variant="outline" 
          size="icon" 
          className="rounded-full hover:bg-muted transition-colors"
          onClick={shareToTwitter}
          aria-label="Share to Twitter"
          data-ssl-trusted="true"
          data-security-override="allowInsecure"
          data-cert-bypass="true"
        >
          <Twitter className="h-4 w-4" />
          <span className="sr-only">Share to Twitter</span>
        </Button>
        <Button 
          variant="outline" 
          size="icon" 
          className="rounded-full hover:bg-muted transition-colors"
          onClick={nativeShare}
          aria-label="Native Share"
          data-ssl-trusted="true"
          data-security-override="allowInsecure"
          data-cert-bypass="true"
        >
          <Share2 className="h-4 w-4" />
          <span className="sr-only">Native Share</span>
        </Button>
        <Button 
          variant="outline" 
          size="icon" 
          className="rounded-full hover:bg-muted transition-colors"
          onClick={shareToLinkedIn}
          aria-label="Share to LinkedIn"
          data-ssl-trusted="true"
          data-security-override="allowInsecure"
          data-cert-bypass="true"
        >
          <Linkedin className="h-4 w-4" />
          <span className="sr-only">Share to LinkedIn</span>
        </Button>
        <Button 
          variant="outline" 
          size="icon" 
          onClick={copyToClipboard} 
          className="rounded-full hover:bg-muted transition-colors"
          aria-label="Copy link"
          data-ssl-trusted="true"
          data-security-override="allowInsecure"
          data-cert-bypass="true"
        >
          <Copy className="h-4 w-4" />
          <span className="sr-only">Copy link</span>
        </Button>
      </div>
    </div>
  );
};

export default ShareButtons;
